import { shouldSkipLocaleMiddleware } from '~/utils/i18n/hydration';

// Client-only: portal pages are CSR and public pages take their locale from the URL on the
// server. Running this on the server would bake per-user redirects into SWR-cached pages.
// (Opted-out, never-cached routes get their server render locale from the cookie in
// plugins/i18n-ssr-cookie-locale.server.ts — render locale only, no redirects.)
export default defineNuxtRouteMiddleware(async (to) => {
  if (import.meta.server) return;
  const nuxtApp = useNuxtApp();
  // The initial client navigation of a server-rendered page runs BEFORE hydration. Nothing may
  // change here yet — neither the locale nor the route — or the client renders different
  // strings/attributes/components than the server HTML (hydration mismatches; attribute
  // mismatches are never patched in production). E.g. an SWR-cached English `/masterclass`
  // opened with a `he` cookie: redirecting to `/he/masterclass` now would hydrate the Hebrew
  // route against English HTML. The server locale is already in place (the URL's for localized
  // routes; for opted-out routes rendered in the cookie locale, plugins/i18n-locale.client.ts
  // adopted it before this runs), so skip unconditionally while hydrating — the plugin's
  // post-hydration sync (`app:suspense:resolve`) performs any preference redirect / locale switch;
  // by then `isHydrating` is false, so that redirect navigation runs this middleware fully
  // (applyLocale for the target). (Disabled-locale URLs are redirected server-side by
  // server/middleware/i18n-disabled-locale.ts.)
  if (shouldSkipLocaleMiddleware({ isHydrating: nuxtApp.isHydrating, serverRendered: nuxtApp.payload.serverRendered }))
    return;
  const { resolveAction, applyAction } = useLocaleSwitch();
  const action = resolveAction(to);
  if (action.type === 'redirect') return navigateTo(action.to, { replace: true });
  await applyAction(action);
});
